import { makeModuleApiInfoSelector, makeModuleI18nSelector, makeModuleStateSelector } from "@client/content/selectors";
import { type FormFieldInfo } from "@client/controls/Form/component";
import NumberInput from "@client/controls/Inputs/NumberInput/component";
import { DynamicSelect } from "@client/controls/Inputs/Select/component";
import { TargetSelector, combinedSettingsSelector, getSettingValueSelector } from "@client/selectors";
import { Status } from "@client/types";
import { createSelector } from "reselect";
import { previewState } from "./reducer";
import { type CheckoutButton } from "./types";

export const defaultState: CheckoutButton.AppState = {
  id: "",
  status: Status.None
};

export const localStateSelector = makeModuleStateSelector<CheckoutButton.AppState>(defaultState, previewState);

export interface AddToCartParams {
  action: "add" | "buy";
  confirmation: "default" | "redirect";
  quantity?: number;
  resetCart: boolean;
  variant?: string;
}
export const makeAddToCartParamsSelector = () =>
  combinedSettingsSelector<AddToCartParams>(TargetSelector.Entity, [
    "action",
    "confirmation",
    "quantity",
    "resetCart",
    "variant"
  ]);

export const makeFieldsSelector = () => {
  const moduleApiInfoSelector = makeModuleApiInfoSelector();
  const moduleI18nSelector = makeModuleI18nSelector();
  const productSelector = getSettingValueSelector(TargetSelector.Entity, "product");
  const quantitySelector = getSettingValueSelector(TargetSelector.Entity, "quantity");
  const variantSelector = getSettingValueSelector(TargetSelector.Entity, "variant");

  return createSelector(
    [productSelector, quantitySelector, variantSelector, moduleApiInfoSelector, moduleI18nSelector],
    (product, quantity, variant, apiInfo, i18n) => {
      const fields: FormFieldInfo[] = [];

      if (!variant) {
        const optionsUrl = product ? "${baseUrl}/api/v1/shop/option/productVariants?productId=" + product : [];
        fields.push({
          name: "variant",
          label: i18n.variant,
          Component: DynamicSelect,
          componentOpts: { apiInfo, dependencies: { product }, options: optionsUrl },
          isRequired: true
        });
      }

      if (!quantity) {
        fields.push({
          name: "quantity",
          label: i18n.quantity,
          Component: NumberInput,
          componentOpts: { min: 1 },
          isRequired: true
        });
      }

      return fields;
    }
  );
};

export const makeProductSelector = () => getSettingValueSelector(TargetSelector.Entity, "product");
